<script setup lang="ts">
/**This is a custom editor that opens in a modal to edit the value of an assembly.
 * In the future it can validate that the variables used are defined.
 */
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import { useDatabaseStore } from '@/stores/databaseStore'
import { computed, onMounted, ref } from 'vue'
import BaseButton from './BaseButton.vue'
const props = defineProps<{
  assemblyItemId: string
}>()

const emit = defineEmits<{
  close: [formula?: string | null]
}>()

const databaseStore = useDatabaseStore()

const assemblyItem = computed(() =>
  databaseStore.assemblyItems?.find((a) => a.id === props.assemblyItemId),
)
const item = computed(() => databaseStore.items?.find((i) => i.id === assemblyItem.value?.item_id))
const formula = ref<string | null>(assemblyItem.value ? assemblyItem.value.formula : '')
const modalRef = ref<HTMLDialogElement | null>(null)

function openModal() {
  modalRef.value?.showModal()
}

function closeModal() {
  modalRef.value?.close()
  emit('close', formula.value)
}

async function saveAndClose() {
  const result = await supabase
    .from('assembly_item')
    .update({ formula: formula.value })
    .eq('id', props.assemblyItemId)
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to update formula', result.error)
  const index = databaseStore.assemblyItems?.findIndex((a) => a.id === props.assemblyItemId)
  if (index === undefined) throw toastThrowError('Unable to update item locally. Refresh the page.')
  databaseStore.assemblyItems?.splice(index, 1, result.data)
  closeModal()
}

onMounted(() => {
  openModal()
})

const showFullScreen = ref(false)
</script>
<template>
  <dialog
    ref="modalRef"
    class="flex w-96 max-w-dvw flex-col gap-3 rounded-lg bg-white p-3 shadow-sm backdrop:bg-black/50 backdrop:opacity-50 open:self-center open:justify-self-center"
    :style="showFullScreen ? { width: '100dvw', height: '100dvh' } : ''"
  >
    <div>
      <div class="flex w-full justify-between">
        <h2 class="text-lg font-bold">Formula Editor</h2>
        <button
          class="cursor-pointer rounded p-1 hover:bg-gray-200"
          @click="showFullScreen = !showFullScreen"
          title="Toggle full screen"
        >
          <svg
            class="size-4 text-gray-800"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              fill-rule="evenodd"
              d="M8 5a1 1 0 0 1 1-1h11a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-1a1 1 0 1 1 0-2h1V6H9a1 1 0 0 1-1-1Z"
              clip-rule="evenodd"
            />
            <path
              fill-rule="evenodd"
              d="M4 7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H4Zm0 11v-5.5h11V18H4Z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
      <p>
        Enter your item formula in the editor below for
        <span class="italic">"{{ item?.name }}"</span>
      </p>
    </div>
    <div>
      <textarea
        v-model="formula"
        rows="8"
        class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
      />
    </div>
    <div class="flex gap-3">
      <BaseButton status="secondary" @click="closeModal()">Cancel</BaseButton>
      <BaseButton @click="saveAndClose()">Save</BaseButton>
    </div>
  </dialog>
</template>
